import React, { useEffect, useState } from 'react';

import {
  CircularProgress, useMediaQuery, useTheme,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';

import DetailsModal, { Values } from '../../components/DetailsModal';
import { LoadingWrapper } from '../../components/PageLayout';
import Tabs from '../../components/Tabs';
import {
  useGetGroupQuery,
  useGetGroupsQuery,
  useNewGroupMutation,
} from '../../services/permissions';
import { ShortPermissionGroup } from '../../services/permissions/types';
import useSuccess from '../../utils/react/hooks/useSuccess';
import useTab from '../../utils/react/hooks/useTab';

import DetailsContent from './DetailsContent';
import {
  AddGroupButton,
  GroupListEntry,
  GroupListEntryText,
  GroupsListContainer,
  GroupsListLoading,
  MainContainer,
  PageContainer,
  SideColumn,
  StickyWrapper,
} from './index.styles';
import MembersContent from './MembersContent';

const TAB_DETAILS = 'Details';
const TAB_MEMBERS = 'Members';

const TAB_OPTIONS = [
  {
    key: TAB_DETAILS,
    label: 'Details',
  },
  {
    key: TAB_MEMBERS,
    label: 'Members',
  },
];

const GROUP_FORM = {
  groupId: 'CPG_',
  name: '',
};

interface GroupsListProps {
  groups: { groupId: string, name: string }[],
  loading: boolean,
  onGroupClick: (groupId: string) => void,
  selectedGroup: string | undefined,
}

const GroupsList = ({
  groups = [],
  loading,
  onGroupClick = () => {},
  selectedGroup,
}: GroupsListProps) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  return (groups.length > 0 || loading) ? (
    <>
      {isMd && (
        <GroupsListContainer>
          {loading && (
            <GroupsListLoading>
              <CircularProgress />
            </GroupsListLoading>
          )}
          {groups.map((g) => (
            <GroupListEntry
              key={g.groupId}
              selected={selectedGroup === g.groupId}
              onClick={() => onGroupClick(g.groupId)}
            >
              <GroupListEntryText
                variant="body2"
                selected={selectedGroup === g.groupId}
              >
                {g.name}
              </GroupListEntryText>
            </GroupListEntry>
          ))}
        </GroupsListContainer>
      )}
      {!isMd && (
        <Tabs
          options={groups.map((g) => ({
            key: g.groupId,
            label: g.name,
          }))}
          selectedKey={selectedGroup || groups?.[0].groupId || ''}
          onChange={(g) => onGroupClick(g)}
          mobileTabs={false}
        />
      )}
    </>
  ) : (
    null
  );
};

const AdminGroupsPage = () => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryGroup = searchParams.get('id');
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  const [newGroupForm, setNewGroupForm] = useState<Values>(GROUP_FORM);
  const [newGroupFormOpen, setNewGroupFormOpen] = useState(false);

  const [sortedGroups, setSortedGroups] = useState<ShortPermissionGroup[]>([]);
  const [group, setGroup] = useState<string | undefined>(undefined);
  const [tab, setTab] = useTab({ default: TAB_OPTIONS[0].key });

  const { data: groups, isLoading } = useGetGroupsQuery({});
  const { data: groupData,
    isLoading: isGroupLoading } = useGetGroupQuery({ group: group || '' }, { skip: !group });
  const [createGroup, {
    isSuccess: isNewGroupSuccess,
    isLoading: isNewGroupLoading,
    error: newGroupError,
    reset: resetNewGroup,
  }] = useNewGroupMutation();

  useEffect(() => {
    if (groups?.data && groups?.data.length > 0) {
      // Once the groups list has loaded
      if (queryGroup && groups?.data
        .map((g: ShortPermissionGroup) => g.groupId)
        .includes(queryGroup)) {
        // If the queried group is valid
        setGroup(queryGroup);
      } else if (!group) {
        // If the selected group is undefined, use the first one in the list
        // alphabetically by the name attribute
        const sorted = ([...groups.data])
          .sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
        const defaultGroup = sorted[0]?.groupId;

        setSortedGroups(sorted);
        setGroup(defaultGroup);
        setSearchParams({
          id: defaultGroup,
        });
      }
    }
  }, [groups, queryGroup]);

  useSuccess(isNewGroupSuccess, 'Group created');

  useEffect(() => {
    if (isNewGroupSuccess) {
      setNewGroupFormOpen(false);
    }
  }, [isNewGroupSuccess]);

  useEffect(() => {
    if (!newGroupFormOpen) {
      setNewGroupForm(GROUP_FORM);
      resetNewGroup();
    }
  }, [newGroupFormOpen]);

  return (
    <PageContainer $isMobile={!isMd}>
      <StickyWrapper $isMobile={!isMd}>
        <SideColumn $isMobile={!isMd}>
          <AddGroupButton
            size="medium"
            onClick={() => setNewGroupFormOpen(true)}
            $isMobile={!isMd}
          >
            + Create Group
          </AddGroupButton>
          <GroupsList
            groups={sortedGroups}
            loading={isLoading}
            onGroupClick={(g) => setSearchParams({ id: g }, { replace: true })}
            selectedGroup={group}
          />
        </SideColumn>
      </StickyWrapper>
      <MainContainer $isMobile={!isMd}>
        <Tabs
          options={TAB_OPTIONS}
          selectedKey={tab}
          onChange={(key) => setTab(key)}
        />
        <LoadingWrapper loading={!group || isGroupLoading}>
          <>
            {tab === TAB_DETAILS && (
              <DetailsContent
                groupId={group || ''}
                group={groupData?.data}
              />
            )}
            {tab === TAB_MEMBERS && (
              <MembersContent
                groupId={group || ''}
                group={groupData?.data}
              />
            )}
          </>
        </LoadingWrapper>
      </MainContainer>
      <DetailsModal
        open={newGroupFormOpen}
        handleClose={() => setNewGroupFormOpen(false)}
        title="Create New Permission Group"
        fields={{
          groupId: {
            label: 'Group ID',
            editable: true,
            type: 'text',
          },
          name: {
            label: 'Group Name',
            editable: true,
            type: 'text',
          },
        }}
        values={newGroupForm}
        error={newGroupError}
        buttons={[{
          fn: async (values?: Values) => {
            const groupId = values?.groupId;
            const name = values?.name;
            await createGroup({ groupId, name });
          },
          label: 'Create',
          color: 'darkPrimary',
          loading: isNewGroupLoading,
        }]}
        onFieldChange={(key, value) => {
          setNewGroupForm({
            ...newGroupForm,
            [key]: value,
          });
        }}
      />
    </PageContainer>
  );
};

export default AdminGroupsPage;
